import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { queryClient } from "@/lib/query";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { PlatformConfigProvider } from "@/lib/platformConfig";
import { Loading } from "@/components/primitives";
import RouteFallback from "@/components/RouteFallback";
import AppLayout from "@/components/AppLayout";

// Auth screens stay eager so the logged-out entry paints instantly.
import Login from "@/pages/Login";
import Register from "@/pages/Register";

// Gates are tiny and wrap most routes — keep them eager.
import BusinessGate from "@/auth/BusinessGate";
import ProGate from "@/auth/ProGate";
import ApprovalGate from "@/auth/ApprovalGate";
import { TourProvider } from "@/help/TourProvider";
import { LiveChatProvider } from "@/live/LiveChatContext";
import UpdateBanner from "@/pwa/UpdateBanner";
import VersionUpdateModal from "@/components/VersionUpdateModal";
import DeployNoticeBanner from "@/components/DeployNoticeBanner";

// Every page below is code-split into its own chunk, loaded on demand with a
// skeleton fallback. This keeps the initial bundle small and navigation fast.
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Home = lazy(() => import("@/pages/Home"));
const Events = lazy(() => import("@/pages/Events"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const EventChat = lazy(() => import("@/pages/EventChat"));
const Community = lazy(() => import("@/pages/Community"));
const PublicEvent = lazy(() => import("@/pages/PublicEvent"));
const Search = lazy(() => import("@/pages/Search"));
const PostDetail = lazy(() => import("@/pages/PostDetail"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const Members = lazy(() => import("@/pages/Members"));
const Groups = lazy(() => import("@/pages/Groups"));
const GroupDetail = lazy(() => import("@/pages/GroupDetail"));
const MemberDetail = lazy(() => import("@/pages/MemberDetail"));
const Connections = lazy(() => import("@/pages/Connections"));
const Intro = lazy(() => import("@/pages/Intro"));
const Messages = lazy(() => import("@/pages/Messages"));
const Chat = lazy(() => import("@/pages/Chat"));
const ForYou = lazy(() => import("@/pages/ForYou"));
const Tickets = lazy(() => import("@/pages/Tickets"));
const Saved = lazy(() => import("@/pages/Saved"));
const MyEvents = lazy(() => import("@/pages/MyEvents"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const Projects = lazy(() => import("@/pages/Projects"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const Docs = lazy(() => import("@/pages/Docs"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const PublicBooking = lazy(() => import("@/pages/PublicBooking"));
const ContractSign = lazy(() => import("@/pages/ContractSign"));
const ApplyPage = lazy(() => import("@/pages/ApplyPage"));
const SupportCenter = lazy(() => import("@/pages/SupportCenter"));
const MyHR = lazy(() => import("@/pages/MyHR"));
const Pro = lazy(() => import("@/pages/Pro"));
const Me = lazy(() => import("@/pages/Me"));
const Settings = lazy(() => import("@/pages/Settings"));
const Business = lazy(() => import("@/pages/Business"));
const BusinessConsole = lazy(() => import("@/pages/BusinessConsole"));
const Activity = lazy(() => import("@/pages/Activity"));
const Scan = lazy(() => import("@/pages/Scan"));
const MyQR = lazy(() => import("@/pages/MyQR"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const CheckoutReturn = lazy(() => import("@/pages/CheckoutReturn"));
const Admin = lazy(() => import("@/pages/Admin"));
const TwoDegreesWorkspace = lazy(() => import("@/pages/TwoDegreesWorkspace"));
const Notes = lazy(() => import("@/pages/Notes"));
const GuestChannel = lazy(() => import("@/pages/GuestChannel"));
const TeamChatApp = lazy(() => import("@/pages/TeamChatApp"));
const Matches = lazy(() => import("@/pages/Matches"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const BusinessOnboarding = lazy(() => import("@/pages/BusinessOnboarding"));
const InvoicePay = lazy(() => import("@/pages/InvoicePay"));
const AcctInvoicePay = lazy(() => import("@/pages/AcctInvoicePay"));
const Receipt = lazy(() => import("@/pages/Receipt"));
const Legal = lazy(() => import("@/pages/Legal"));
const Membership = lazy(() => import("@/pages/Membership"));
const Account = lazy(() => import("@/pages/Account"));
const Help = lazy(() => import("@/pages/Help"));
const HowToUse = lazy(() => import("@/pages/HowToUse"));
const Social = lazy(() => import("@/pages/Social"));
const Digest = lazy(() => import("@/pages/Digest"));
const Rewards = lazy(() => import("@/pages/Rewards"));
const Learn = lazy(() => import("@/pages/Learn"));
const LearnCatalog = lazy(() => import("@/pages/LearnCatalog"));
const CourseDetail = lazy(() => import("@/pages/CourseDetail"));
const CoursePlayer = lazy(() => import("@/pages/CoursePlayer"));
const CourseBuilder = lazy(() => import("@/pages/CourseBuilder"));
const MyCourses = lazy(() => import("@/pages/MyCourses"));
const LiveSessions = lazy(() => import("@/pages/LiveSessions"));
const LiveSessionDetail = lazy(() => import("@/pages/LiveSessionDetail"));
const LiveRoom = lazy(() => import("@/pages/LiveRoom"));
const EventLiveRoom = lazy(() => import("@/pages/EventLiveRoom"));
const LiveBuilder = lazy(() => import("@/pages/LiveBuilder"));
const Store = lazy(() => import("@/pages/Store"));
const Press = lazy(() => import("@/pages/Press"));
const PressArticle = lazy(() => import("@/pages/Press").then((m) => ({ default: m.PressArticle })));
const PressEditor = lazy(() => import("@/pages/Press").then((m) => ({ default: m.PressEditor })));
const PressMine = lazy(() => import("@/pages/Press").then((m) => ({ default: m.PressMine })));
const StoreOrders = lazy(() => import("@/pages/StoreOrders"));
const StoreOrderConfirmation = lazy(() => import("@/pages/StoreOrders").then((m) => ({ default: m.StoreOrderConfirmation })));

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-surface"><Loading label="Loading…" /></div>;
  if (!user) {
    // Keep the intended destination in the URL (?next=) as well as router state, so it
    // survives a full reload or an installed-PWA browser handoff and we can return the
    // user straight to where they were headed (e.g. /team-chat) after they log in.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace state={{ from: location }} />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-surface"><Loading /></div>;
  if (user) {
    // Honor where the user was headed before being bounced to login (e.g. the chat PWA
    // sends them to /team-chat), so logging in doesn't dump them on the home page.
    const params = new URLSearchParams(location.search);
    const raw = location.state?.from
      ? `${location.state.from.pathname}${location.state.from.search || ""}`
      : params.get("next");
    const dest = raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function AdminOnly({ children }) {
  const { user, previewMember } = useAuth();
  if (!user?.is_admin || previewMember) return <Navigate to="/" replace />;
  return children;
}

function TdBusinessOnly({ children }) {
  const { user, previewMember } = useAuth();
  if (!user?.td_business_access || previewMember) return <Navigate to="/" replace />;
  return children;
}

// Blocks admin/owner-only pages while an admin is "previewing as member", so the
// preview faithfully mirrors what a regular member can reach.
function NotWhilePreview({ children }) {
  const { previewMember } = useAuth();
  if (previewMember) return <Navigate to="/" replace />;
  return children;
}

function OnboardingGate({ children }) {
  const { user } = useAuth();
  const needs = user && user.account_type !== "business" && !user.profile_complete
    && !user.title && !user.company && !user.bio;
  if (needs) return <Navigate to="/onboarding" replace />;
  if (user && user.account_type === "business" && !user.biz_onboarded) return <Navigate to="/business-onboarding" replace />;
  return children;
}

// Warm up the chunks for the screens members hit most, once the browser is
// idle, so the first navigation to them is instant.
function usePrefetchCommonRoutes() {
  useEffect(() => {
    const run = () => {
      import("@/pages/Events");
      import("@/pages/Community");
      import("@/pages/Members");
      import("@/pages/Messages");
      import("@/pages/Me");
      import("@/pages/Notifications");
    };
    const ric = window.requestIdleCallback;
    const id = ric ? ric(run) : setTimeout(run, 2000);
    return () => { if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id); else clearTimeout(id); };
  }, []);
}

// Report uncaught client errors to analytics (throttled, fire-and-forget).
function useCrashReporter() {
  useEffect(() => {
    let last = 0;
    const report = (message, stack) => {
      const now = Date.now();
      if (now - last < 10000) return;
      last = now;
      import("@/lib/api").then(({ api }) => api("/analytics/crash", {
        method: "POST",
        body: { platform: "web", message: String(message || "").slice(0, 500), route: window.location?.pathname, stack: String(stack || "").slice(0, 2000) },
      }).catch(() => {}));
    };
    const onErr = (e) => report(e?.message || "error", e?.error?.stack);
    const onRej = (e) => report(e?.reason?.message || "unhandledrejection", e?.reason?.stack);
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => { window.removeEventListener("error", onErr); window.removeEventListener("unhandledrejection", onRej); };
  }, []);
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/join" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/checkout-return" element={<Protected><CheckoutReturn /></Protected>} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/business-onboarding" element={<Protected><BusinessOnboarding /></Protected>} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/membership" element={<Membership />} />
      <Route path="/guest/:token" element={<GuestChannel />} />
      <Route path="/team-chat" element={<Protected><BusinessGate><TeamChatApp /></BusinessGate></Protected>} />
      <Route path="/e/:id" element={<PublicEvent />} />
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/sign/:token" element={<ContractSign />} />
      <Route path="/apply/:token" element={<ApplyPage />} />
      <Route path="/pay/acct/:token" element={<AcctInvoicePay />} />

      <Route element={<Protected><ApprovalGate><OnboardingGate><AppLayout /></OnboardingGate></ApprovalGate></Protected>}>
        <Route path="/" element={<Home />} />
        <Route path="/events" element={<Events />} />
        <Route path="/event/:id" element={<EventDetail />} />
        <Route path="/event-chat/:id" element={<EventChat />} />
        <Route path="/event-live/:id" element={<EventLiveRoom />} />
        <Route path="/community" element={<Community />} />
        <Route path="/search" element={<Search />} />
        <Route path="/post/:id" element={<PostDetail />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/members" element={<Members />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/groups/:id" element={<GroupDetail />} />
        <Route path="/member/:userId" element={<MemberDetail />} />
        <Route path="/connections" element={<Connections />} />
        <Route path="/intro/:userId" element={<Intro />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/chat/:userId" element={<Chat />} />
        <Route path="/for-you" element={<ForYou />} />
        <Route path="/matches" element={<Matches />} />
        <Route path="/tickets" element={<Tickets />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/wallet-return" element={<Wallet />} />
        <Route path="/saved" element={<Saved />} />
        <Route path="/my-events" element={<MyEvents />} />
        <Route path="/account" element={<Account />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/projects" element={<BusinessGate featureKey="projects"><Projects /></BusinessGate>} />
        <Route path="/projects/:pid" element={<BusinessGate featureKey="projects"><ProjectDetail /></BusinessGate>} />
        <Route path="/docs" element={<BusinessGate featureKey="docs"><Docs /></BusinessGate>} />
        <Route path="/docs/:docId" element={<BusinessGate featureKey="docs"><Docs /></BusinessGate>} />
        <Route path="/calendar" element={<BusinessGate featureKey="calendar"><Calendar /></BusinessGate>} />
        <Route path="/my-hr" element={<BusinessGate featureKey="hr" allowUserKey="hr_linked"><MyHR /></BusinessGate>} />
        <Route path="/pro" element={<Pro />} />
        <Route path="/me" element={<Me />} />
        <Route path="/notes" element={<Notes />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/business" element={<Business />} />
        <Route path="/manage-postings" element={<Navigate to="/business/console" replace />} />
        <Route path="/history" element={<Activity />} />
        <Route path="/scan" element={<Scan />} />
        <Route path="/my-qr" element={<MyQR />} />
        <Route path="/help" element={<Help />} />
        <Route path="/how-to-use" element={<HowToUse />} />
        <Route path="/support-center" element={<SupportCenter />} />
        <Route path="/support" element={<Navigate to="/help" replace />} />
        <Route path="/digest" element={<Digest />} />
        <Route path="/rewards" element={<Rewards />} />
        <Route path="/learn" element={<ProGate featureKey="learning"><Learn /></ProGate>} />
        <Route path="/learn/catalog" element={<ProGate featureKey="learning"><LearnCatalog /></ProGate>} />
        <Route path="/learn/manage" element={<ProGate featureKey="learning"><MyCourses /></ProGate>} />
        <Route path="/learn/live" element={<ProGate featureKey="learning"><LiveSessions /></ProGate>} />
        <Route path="/learn/live/new" element={<ProGate featureKey="learning"><LiveBuilder /></ProGate>} />
        <Route path="/learn/live/edit/:id" element={<ProGate featureKey="learning"><LiveBuilder /></ProGate>} />
        <Route path="/learn/live/:id" element={<ProGate featureKey="learning"><LiveSessionDetail /></ProGate>} />
        <Route path="/learn/live/:id/room" element={<ProGate featureKey="learning"><LiveRoom /></ProGate>} />
        <Route path="/learn/new" element={<ProGate featureKey="learning"><CourseBuilder /></ProGate>} />
        <Route path="/learn/edit/:id" element={<ProGate featureKey="learning"><CourseBuilder /></ProGate>} />
        <Route path="/learn/course/:id" element={<ProGate featureKey="learning"><CourseDetail /></ProGate>} />
        <Route path="/learn/course/:id/learn" element={<ProGate featureKey="learning"><CoursePlayer /></ProGate>} />
        <Route path="/social" element={<ProGate featureKey="social"><Social /></ProGate>} />
        <Route path="/store" element={<Store />} />
        <Route path="/store/orders" element={<StoreOrders />} />
        <Route path="/store/order/:sessionId" element={<StoreOrderConfirmation />} />
        <Route path="/press" element={<Press />} />
        <Route path="/press/new" element={<PressEditor />} />
        <Route path="/press/mine" element={<PressMine />} />
        <Route path="/press/:id/edit" element={<PressEditor />} />
        <Route path="/press/:id" element={<PressArticle />} />
        <Route path="/pay/invoice/:token" element={<InvoicePay />} />
        <Route path="/receipt/:invoiceId" element={<Receipt />} />
      </Route>

      <Route path="/business/console" element={<Protected><ApprovalGate><NotWhilePreview><BusinessConsole /></NotWhilePreview></ApprovalGate></Protected>} />
      <Route path="/admin" element={<Protected><AdminOnly><Admin /></AdminOnly></Protected>} />
      <Route path="/two-degrees" element={<Protected><TdBusinessOnly><NotWhilePreview><TwoDegreesWorkspace /></NotWhilePreview></TdBusinessOnly></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// The installed "2° Chat" PWA should always open to Team Chat. iOS/Android sometimes
// cold-launch a home-screen app at "/" (e.g. an icon installed before the manifest
// start_url was correct), so we self-heal: once this install is known to be the chat
// app, every cold launch/resume routes straight to /team-chat.
// - We mark the install as the chat app when launched via its start_url marker (?pwa=1).
// - On iOS home-screen apps (navigator.standalone), storage is isolated PER installed
//   app, so visiting /team-chat there also marks it — this fixes an already-installed
//   icon without a reinstall, and cannot affect the separate main-app icon.
function ChatPwaLauncher() {
  const navigate = useNavigate();
  useEffect(() => {
    let standalone = false;
    let iosStandalone = false;
    try {
      standalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
      iosStandalone = window.navigator.standalone === true;
    } catch { /* ignore */ }
    if (!standalone && !iosStandalone) return;
    const path = window.location.pathname;
    const isPwaMarker = new URLSearchParams(window.location.search).get("pwa") === "1";
    try {
      if (isPwaMarker || (iosStandalone && path.startsWith("/team-chat"))) {
        localStorage.setItem("td_chat_pwa", "1");
      }
      if (localStorage.getItem("td_chat_pwa") === "1" && (path === "/" || path === "")) {
        navigate("/team-chat", { replace: true });
      }
    } catch { /* ignore */ }
  }, [navigate]);
  return null;
}


function AppInner() {
  usePrefetchCommonRoutes();
  useCrashReporter();
  return (
    <Suspense fallback={<RouteFallback />}>
      <ChatPwaLauncher />
      <AppRoutes />
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PlatformConfigProvider>
        <AuthProvider>
          <BrowserRouter>
            <TourProvider>
              <LiveChatProvider>
                <AppInner />
              </LiveChatProvider>
            </TourProvider>
            <Toaster position="top-center" richColors />
            <UpdateBanner />
            <DeployNoticeBanner />
            <VersionUpdateModal />
          </BrowserRouter>
        </AuthProvider>
      </PlatformConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
